import { lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { isAuthenticated } from '../hooks/CookiesHook'
import {
  getFirstAllowedNaiFlowDashboardPath,
  getLocalStorage,
  hasPermission,
  homeScreenRedirection,
  naiFlowAuthTab,
} from '../utils/helper'

// Lazy load pages
const PageNotFound = lazy(() => import('../pages/PageNotFound'))
const Login = lazy(() => import('../pages/Login'))
const OTPVerification = lazy(() => import('../pages/OTPVerification'))
const SelectWorkspace = lazy(() => import('../pages/SelectWorkspace'))
const ChangeWorkspace = lazy(() => import('../pages/ChangeWorkspace'))
// Dashboard
const Dashboard = lazy(() => import('../pages/Dashboard'))
const ObservationsListing = lazy(() => import('../pages/Dashboard/Listings/Observations'))
const IncidentsListing = lazy(() => import('../pages/Dashboard/Listings/Incidents'))
const InspectionsListing = lazy(() => import('../pages/Dashboard/Listings/Inspections'))
const TrainingsListing = lazy(() => import('../pages/Dashboard/Listings/Trainings'))
const SiteWalkthroughDashboard = lazy(() => import('../pages/Dashboard/SiteWalkthroughDashboard'))
const StatisticsCaptureDashboard = lazy(() => import('../pages/Dashboard/StatisticsCaptureDashboard'))
const IncidentDashboard = lazy(() => import('../pages/Dashboard/IncidentDashboard'))
const InspectionDashboard = lazy(() => import('../pages/Dashboard/InspectionDashboard'))
const TrainingDashboard = lazy(() => import('../pages/Dashboard/TrainingDashboard'))
const TrainingDashboardNew = lazy(() => import('../pages/Dashboard/TrainingDashboardNew'))
const OverviewDashboard = lazy(() => import('../pages/Dashboard/OverviewDashboard'))
const OverviewDashboardNew = lazy(() => import('../pages/Dashboard/OverviewDashboardNew'))
const ObservationDashboardNew = lazy(() => import('../pages/Dashboard/ObservationDashboardNew'))
const IncidentDashboardNew = lazy(() => import('../pages/Dashboard/IncidentDashboardNew'))

/** Redirects /dashboard/nai-flow to the first nAI Flow tab the user has permission for. */
const NaiFlowDashboardRedirect = () => <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />

/** If user has no Observation tab permission, redirect to first allowed nAI Flow tab instead of rendering ObservationDashboardNew. */
const ObservationRouteGuard = () => {
  const permissions = JSON.parse(getLocalStorage('permissions') || '[]')
  if (!hasPermission(permissions, 'observations-dashboard', 1)) {
    return <Navigate to={getFirstAllowedNaiFlowDashboardPath()} replace />
  }
  return (
    <TabsLayout
      breadcrumbData={[
        { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
        { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
        { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
      ]}
      page="nai-flow-dashboard"
    >
      <ObservationDashboardNew />
    </TabsLayout>
  )
}

// Nai Hub Dashboard
const NaiHubDashboard = lazy(() => import('../pages/Dashboard/NaiHubDashboard'))
// User Management
const UserRole = lazy(() => import('../pages/UserManagement/UserRole'))
const UserListing = lazy(() => import('../pages/UserManagement/UserListing'))
const AddUserRole = lazy(() => import('../pages/UserManagement/AddUserRole'))
const AddUser = lazy(() => import('../pages/UserManagement/AddUser'))
// Quiz Assessment
const QuizAssessment = lazy(() => import('../pages/QuizAssessment'))
const QuizAssessmentDetails = lazy(() => import('../pages/QuizAssessment/QuizAssessmentDetails'))
// Video Script Generation
const VideoScriptGeneration = lazy(() => import('../pages/VideoScriptGeneration'))
// Project Management Start
const ProjectManagement = lazy(() => import('../pages/ProjectManagement'))
const AddProject = lazy(() => import('../pages/ProjectManagement/AddProject'))
// Project's Rules And Escalation
const RulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation'))
const AddRulesAndEscalation = lazy(() => import('../pages/ProjectManagement/RulesAndEscalation/AddRulesAndEscalation'))
// Project's User Management
const ProjectUsers = lazy(() => import('../pages/ProjectManagement/ProjectUsers'))
const AddUserToProject = lazy(() => import('../pages/ProjectManagement/ProjectUsers/AddUserToProject'))
const ProjectRoles = lazy(() => import('../pages/ProjectManagement/ProjectRoles'))
const AddRoleToProject = lazy(() => import('../pages/ProjectManagement/ProjectRoles/AddRoleToProject'))
// nAI Flow
const WhatsappGroup = lazy(() => import('../pages/ProjectManagement/NaiFlow/WhatsappGroup'))
const Observations = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations'))
const Incidents = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents'))
const ObservationDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Observations/ObservationDetails'))
const IncidentDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Incidents/IncidentDetails'))
const Inspections = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections'))
const NonConformance = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance'))
const NonConformanceDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/InspectionDetails'))
const Schedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule'))
const AddInspectionSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/Inspections/Schedule/AddInspectionSchedule'))
const Trainings = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings'))
const TrainingDetails = lazy(() => import('../pages/ProjectManagement/NaiFlow/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCapture = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule'))
const AddSchedule = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport'))
const EditReport = lazy(() => import('../pages/ProjectManagement/NaiFlow/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Flow VIew
const WhatsappGroupView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/WhatsappGroup'))
const ObservationsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations'))
const IncidentsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents'))
const ObservationDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Observations/ObservationDetails'))
const IncidentDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Incidents/IncidentDetails'))
const InspectionsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections'))
const NonConformanceView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance'))
const NonConformanceDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/NonConformance/NonConformanceDetails'))
const InspectionDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/InspectionDetails'))
const ScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule'))
const AddInspectionScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Inspections/Schedule/AddInspectionSchedule'))
const TrainingsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings'))
const TrainingDetailsView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/Trainings/TrainingDetails'))
// statistics Capture
const StatisticsCaptureView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule'))
const AddScheduleView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/Schedule/AddSchedule'))
const StatisticsCaptureReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport'))
const EditReportView = lazy(() => import('../pages/ProjectManagement/NaiFlowLinkedProject/StatisticsCapture/StatisticsCaptureReport/EditReport'))

// nAI Hub
const NaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub'))
const AddNaiHub = lazy(() => import('../pages/ProjectManagement/NaiHub/AddNaiHub'))
// const NaiHubDashboard = lazy(() => import('../pages/ProjectManagement/NaiHub/NaiHubDashboard'))

const ProjectWorkflow = lazy(() => import('../pages/ProjectManagement/ProjectWorkflow'))
// Project Management End

// Masters pages
const Division = lazy(() => import('../pages/Masters/Division'))
const HazardType = lazy(() => import('../pages/Masters/HazardType'))
const RootCause = lazy(() => import('../pages/Masters/RootCause'))
const Severity = lazy(() => import('../pages/Masters/Severity'))
const ObservationStatus = lazy(() => import('../pages/Masters/ObservationStatus'))
const StatisticsCaptureStatus = lazy(() => import('../pages/Masters/StatisticsCaptureStatus'))
const StatisticsCaptureInputs = lazy(() => import('../pages/Masters/StatisticsCaptureInputs'))
const IncidentStatus = lazy(() => import('../pages/Masters/IncidentStatus'))
const IncidentCategory = lazy(() => import('../pages/Masters/IncidentCategory'))
const IncidentSeverity = lazy(() => import('../pages/Masters/IncidentSeverity'))
const InspectionStatus = lazy(() => import('../pages/Masters/InspectionStatus'))
const InspectionScheduleStatus = lazy(() => import('../pages/Masters/InspectionScheduleStatus'))
const InspectionNonConformanceStatus = lazy(() => import('../pages/Masters/InspectionNonConformanceStatus'))
const InspectionSubCategory = lazy(() => import('../pages/Masters/InspectionSubCategory'))
const InspectionTopic = lazy(() => import('../pages/Masters/InspectionTopic'))
const InspectionQuestions = lazy(() => import('../pages/Masters/InspectionQuestions'))
const TrainingCategory = lazy(() => import('../pages/Masters/TrainingCategory'))

// Lazy load components
const DashboardLayout = lazy(() => import('../components/UI/Layouts/DashboardLayout'))
const LRFLayout = lazy(() => import('../components/UI/Layouts/LRFLayout'))
const ProjectLayout = lazy(() => import('../components/UI/Layouts/ProjectLayout'))
const TabsLayout = lazy(() => import('../components/UI/Layouts/TabsLayout'))
const UserManagementLayout = lazy(() => import('../components/UI/Layouts/UserManagementLayout'))
const CopilotLayout = lazy(() => import('../components/UI/Layouts/CopilotLayout'))
const ComingSoon = lazy(() => import('../components/ComingSoon'))
const CheckAppInstalled = lazy(() => import('../pages/CheckAppInstalled'))
const SSO = lazy(() => import('../pages/SSO'))

// if already authenticated and try to access login page, redirect to dashboard
const PublicRoute = ({ children }: any) => {
  const permission_details = getLocalStorage('permissions') ? JSON.parse(`${getLocalStorage('permissions')}`) : []

  let redirectPath = homeScreenRedirection()
  const paths = [
    { path: naiFlowAuthTab, slug: 'dashboard' },
    { path: '/reports', slug: 'reports' },
    { path: '/user-management/user-role', slug: 'user-management' },
    { path: '/user-management/user-role', slug: 'user' },
    { path: '/project-management', slug: 'project-management' },
    { path: '/project-management', slug: 'project-list' },
  ] as any
  if (!permission_details?.find((p: any) => p?.slug === 'observations')) {
    const firstSlug = permission_details[0]?.slug
    if (paths?.find((p: any) => p?.slug === firstSlug) && firstSlug) {
      redirectPath = paths?.find((p: any) => p?.slug === firstSlug)?.path
    }
  }

  return isAuthenticated() ? <Navigate to={redirectPath} /> : children
}

// if not authenticated and try to access protected page, redirect to login
const ProtectedRoute = ({ children }: any) => {
  return isAuthenticated() ? children : <Navigate to="/login" />
}

const wrapRoutesWithProtectedRoute = (routes: any) => {
  // Map over the routes array
  return routes.map((route: any) => {
    // Check if the route has children
    if (route.children) {
      // If it has children, recursively wrap the children with protected route
      const children = wrapRoutesWithProtectedRoute(route.children)
      return {
        ...route,
        children,
      }
    } else {
      // If the route does not have children, determine whether to wrap it with protected or public route
      return {
        ...route,
        element: route?.private ? <ProtectedRoute key={route.path}>{route.element}</ProtectedRoute> : <PublicRoute key={route.path}>{route.element}</PublicRoute>,
      }
    }
  })
}

export const routes = [
  ...wrapRoutesWithProtectedRoute([
    {
      path: '',
      private: false,
      element: (
        <LRFLayout>
          <Login />
        </LRFLayout>
      ),
    },
    {
      path: 'login',
      private: false,
      element: (
        <LRFLayout>
          <Login />
        </LRFLayout>
      ),
    },
    {
      path: 'otp-verification',
      private: false,
      element: (
        <LRFLayout>
          <OTPVerification />
        </LRFLayout>
      ),
    },
    {
      path: 'select-workspace',
      private: false,
      element: (
        <LRFLayout>
          <SelectWorkspace />
        </LRFLayout>
      ),
    },
    {
      path: 'change-workspace',
      private: true,
      element: (
        <DashboardLayout>
          <ChangeWorkspace />
        </DashboardLayout>
      ),
    },
    // New Dashboard flow
    {
      path: 'dashboard/overview',
      private: true,
      element: (
        <DashboardLayout
          page={'nai-flow-dashboard'}
        >
          <OverviewDashboardNew />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/overview-old',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/observation' },
            { title: 'Overview', redirect: '/dashboard/overview-old' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <OverviewDashboard />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/observation-old',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow/observation' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow/observation' },
            { title: 'Observation', redirect: '/dashboard/nai-flow/observation' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <Dashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow',
      private: true,
      element: <NaiFlowDashboardRedirect />,
    },
    {
      path: 'dashboard/nai-flow/observation',
      private: true,
      element: <ObservationRouteGuard />,
    },
    {
      path: 'dashboard/nai-flow/observations-listing',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Observations', redirect: '/dashboard/nai-flow/observations-listing' },
          ]}
        >
          <ObservationsListing />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/incidents-listing',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow/incident' },
            { title: 'Incidents', redirect: '/dashboard/nai-flow/incidents-listing' },
          ]}
        >
          <IncidentsListing />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/inspections-listing',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow/inspection' },
            { title: 'Inspections', redirect: '/dashboard/nai-flow/inspections-listing' },
          ]}
        >
          <InspectionsListing />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/trainings-listing',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow/training' },
            { title: 'Trainings', redirect: '/dashboard/nai-flow/trainings-listing' },
          ]}
        >
          <TrainingsListing />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'Observations', redirect: '/dashboard' },
          ]}
        >
          <Dashboard />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/site-walkthrough',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard' },
            { title: 'Site Walkthrough', redirect: '/dashboard' },
          ]}
        >
          <SiteWalkthroughDashboard />
        </DashboardLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/statistics-capture',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Statistics Capture', redirect: '/dashboard/nai-flow/statistics-capture' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <StatisticsCaptureDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/incident',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Incident', redirect: '/dashboard/nai-flow/incident' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <IncidentDashboardNew />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/incident-old',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Incident (Old)', redirect: '/dashboard/nai-flow/incident-old' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <IncidentDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/inspection',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Inspection', redirect: '/dashboard/nai-flow/inspection' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <InspectionDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/training',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Training', redirect: '/dashboard/nai-flow/training' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <TrainingDashboardNew />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-flow/training-old',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Flow', redirect: '/dashboard/nai-flow' },
            { title: 'Training (Old)', redirect: '/dashboard/nai-flow/training-old' },
          ]}
          page={'nai-flow-dashboard'}
        >
          <TrainingDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'dashboard/nai-hub',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Hub', redirect: '/dashboard/nai-hub' },
          ]}
        >
          <ComingSoon />
        </DashboardLayout>
      ),
    },
    // NaiHub Dashboard
    {
      path: 'dashboard/nai-hub/:name/:id',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Dashboard', redirect: '/dashboard/nai-flow' },
            { title: 'nAI Hub', redirect: '/dashboard/nai-hub/observation' },
          ]}
          page={'nai-hub-dashboard'}
        >
          <NaiHubDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'reports',
      private: true,
      element: (
        <DashboardLayout>
          <div className="h-full p-8">
            <ComingSoon pageName="Statistics Capture" />
          </div>
        </DashboardLayout>
      ),
    },
    {
      path: 'user-management/user-role',
      private: true,
      element: (
        <UserManagementLayout
          defaultIndex={1}
          breadcrumbData={[
            { title: 'User Management', redirect: '/user-management/user-role' },
            { title: 'User Role', redirect: '/user-management/user-role' },
          ]}
        >
          <UserRole />
        </UserManagementLayout>
      ),
    },
    {
      path: 'user-management/user-role/:id',
      private: true,
      element: (
        <UserManagementLayout
          defaultIndex={1}
          breadcrumbData={[
            { title: 'User Management', redirect: '/user-management/user-role' },
            { title: 'User Role', redirect: '/user-management/user-role' },
          ]}
        >
          <AddUserRole />
        </UserManagementLayout>
      ),
    },
    {
      path: 'user-management/users',
      private: true,
      element: (
        <UserManagementLayout
          defaultIndex={0}
          breadcrumbData={[
            { title: 'User Management', redirect: '/user-management/users' },
            { title: 'Users', redirect: '/user-management/users' },
          ]}
        >
          <UserListing />
        </UserManagementLayout>
      ),
    },
    {
      path: 'user-management/users/:id',
      private: true,
      element: (
        <UserManagementLayout
          defaultIndex={0}
          breadcrumbData={[
            { title: 'User Management', redirect: '/user-management/users' },
            { title: 'Users', redirect: '/user-management/users' },
          ]}
        >
          <AddUser />
        </UserManagementLayout>
      ),
    },
    // Quiz Assessment
    {
      path: 'quiz-assessment',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Quiz Assessment', redirect: '/quiz-assessment' },
          ]}
        >
          <QuizAssessment />
        </DashboardLayout>
      ),
    },
    {
      path: 'quiz-assessment/:id',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Quiz Assessment', redirect: '/quiz-assessment' },
            { title: 'Details', redirect: '/quiz-assessment/:id' },
          ]}
        >
          <QuizAssessmentDetails />
        </DashboardLayout>
      ),
    },
    // Video Script Generation (permission slug: video-script-generation)
    {
      path: 'video-script-generation',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Video Script Generation', redirect: '/video-script-generation' },
          ]}
        >
          <VideoScriptGeneration />
        </DashboardLayout>
      ),
    },
    //Project Routes
    {
      path: 'project-management',
      private: true,
      element: (
        <DashboardLayout>
          <ProjectManagement />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/details',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Details', redirect: '/project-management/:name/:id/details' },
          ]}
          page={'project-page'}
        >
          <AddProject mode={'detail'} />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/workflow',
      private: true,
      element: (
        <ProjectLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Workflow', redirect: '/project-management/:name/:id/details' },
          ]}
        >
          <ProjectWorkflow />
        </ProjectLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/rules-and-escalation',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
          ]}
          page={'project-page'}
        >
          <RulesAndEscalation />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/rules-and-escalation/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
            { title: 'Add', redirect: '/project-management/:name/:id/rules-and-escalation' },
          ]}
          page={'project-page'}
        >
          <AddRulesAndEscalation />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/rules-and-escalation/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
            { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
          ]}
          page={'project-page'}
        >
          <AddRulesAndEscalation />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/rules-and-escalation/duplicate/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'Rules And Escalation', redirect: '/project-management/:name/:id/rules-and-escalation' },
            { title: ':subName', redirect: '/project-management/:name/:id/rules-and-escalation' },
          ]}
          page={'project-page'}
        >
          <AddRulesAndEscalation isDuplicate={true} />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/users',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Users', redirect: '/project-management/:name/:id/details' },
          ]}
          page={'project-user-management'}
        >
          <ProjectUsers />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/users/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
            { title: 'Add', redirect: '/project-management/:name/:id/user-management/users' },
          ]}
          page={'project-user-management'}
        >
          <AddUserToProject />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/users/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Users', redirect: '/project-management/:name/:id/user-management/users' },
            { title: ':subName', redirect: '/project-management/:name/:id/user-management/users' },
          ]}
          page={'project-user-management'}
        >
          <AddUserToProject />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/roles',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Roles', redirect: '/project-management/:name/:id/details' },
          ]}
          page={'project-user-management'}
        >
          <ProjectRoles />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/roles/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
            { title: 'Add', redirect: '/project-management/:name/:id/user-management/roles' },
          ]}
          page={'project-user-management'}
        >
          <AddRoleToProject />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/user-management/roles/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'User Management', redirect: '/project-management/:name/:id/user-management/:slug' },
            { title: 'Roles', redirect: '/project-management/:name/:id/user-management/roles' },
            { title: ':subName', redirect: '/project-management/:name/:id/user-management/roles' },
          ]}
          page={'project-user-management'}
        >
          <AddRoleToProject />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/whatsapp-group',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
          ]}
          page={'nai-flow'}
        >
          <WhatsappGroup />
        </TabsLayout>
      ),
    },
    /*  {
       path: 'project-management/:name/:id/nai-flow-view/whatsapp-group',
       private: true,
       element: (
         <TabsLayout
           breadcrumbData={[
             { title: 'Project Management', redirect: '/project-management' },
             { title: ':name', redirect: '/project-management/:name/:id/details' },
             { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
             { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/nai-flow' },
           ]}
           page={'nai-flow-view'}
         >
           <WhatsappGroupView />
         </TabsLayout>
       ),
     }, */
    {
      path: 'project-management/:name/:id/nai-flow/observations',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/observations' },
          ]}
          page={'nai-flow'}
        >
          <Observations />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/observations',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
          ]}
          page={'nai-flow-view'}
        >
          <ObservationsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/observations/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/Observations' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
          ]}
          page={'nai-flow-view'}
          disableBreadcrumbLinks={true}
        >
          <ObservationDetailsView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Incidents
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
          ]}
          page={'nai-flow-view'}
        >
          <IncidentsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
            { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/incidents' },
          ]}
          page={'nai-flow-view'}
          disableBreadcrumbLinks={true}
        >
          <IncidentDetailsView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Inspections
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <ScheduleView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <InspectionsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Non-Conformance', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/non-conformance' },
          ]}
          page={'nai-flow-view'}
        >
          <NonConformanceView />
        </TabsLayout>
      ),
    },
    // Linked Projects - Trainings
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
          ]}
          page={'nai-flow-view'}
        >
          <TrainingsView />
        </TabsLayout>
      ),
    },
    // Linked Projects - Training Details
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
            { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/trainings' },
          ]}
          page={'nai-flow-view'}
          disableBreadcrumbLinks={true}
        >
          <TrainingDetailsView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Inspection Details
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
            { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/list' },
          ]}
          page={'nai-flow-view'}
        >
          <InspectionDetailsView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Statistics Capture
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <StatisticsCaptureView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
            { title: 'Reports', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/reports' },
          ]}
          page={'nai-flow-view'}
        >
          <StatisticsCaptureReportView />
        </TabsLayout>
      ),
    },
    // Linked Projects - Add Inspection Schedule
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/inspections/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <AddInspectionScheduleView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Add Statistics Capture Schedule
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <AddScheduleView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Statistics Capture Schedule Details
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
            { title: ':subName', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <StatisticsCaptureView />
        </DashboardLayout>
      ),
    },
    // Linked Projects - Whatsapp Group
    {
      path: 'project-management/:name/:id/:subsidiaryId/nai-flow-view/whatsapp-group',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/:slug' },
            { title: 'Whatsapp Group', redirect: '/project-management/:name/:id/:subsidiaryId/nai-flow-view/whatsapp-group' },
          ]}
          page={'nai-flow-view'}
        >
          <WhatsappGroupView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/observations/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow/Observations' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/observations' },
          ]}
          page={'nai-flow'}
        >
          <ObservationDetails />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/observations/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Observations', redirect: '/project-management/:name/:id/nai-flow-view/Observations' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/observations' },
          ]}
          page={'nai-flow-view'}
        >
          <ObservationDetailsView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/incidents',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
          ]}
          page={'nai-flow'}
        >
          <Incidents />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/incidents',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
          ]}
          page={'nai-flow-view'}
        >
          <IncidentsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/incidents/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow/incidents' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/incidents' },
          ]}
          page={'nai-flow'}
        >
          <IncidentDetails />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/incidents/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Incidents', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/incidents' },
          ]}
          page={'nai-flow-view'}
        >
          <IncidentDetailsView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/list',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow'}
        >
          <Inspections />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/list',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow-view'}
        >
          <InspectionsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/list/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/list' },
          ]}
          page={'nai-flow'}
        >
          <InspectionDetails />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/list/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Inspections List', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/list' },
          ]}
          page={'nai-flow-view'}
        >
          <InspectionDetailsView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow'}
        >
          <Schedule />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow-view'}
        >
          <ScheduleView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
          ]}
        >
          <AddInspectionSchedule />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
          ]}
        >
          <AddInspectionScheduleView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/schedule/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
          ]}
          page={'nai-flow'}
        >
          <AddInspectionSchedule />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/schedule/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <AddInspectionScheduleView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/non-conformance',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow'}
        >
          <NonConformance />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/non-conformance',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
          ]}
          page={'inspections'}
          parentPage={'nai-flow-view'}
        >
          <NonConformanceView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/inspections/non-conformance/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow/Inspections/schedule' },
            { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/Inspections/non-conformance' },
          ]}
        >
          <NonConformanceDetails />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/inspections/non-conformance/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Inspections', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/schedule' },
            { title: 'Non-Conformance', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/Inspections/non-conformance' },
          ]}
        >
          <NonConformanceDetailsView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/trainings',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
          ]}
          page={'nai-flow'}
        >
          <Trainings />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/trainings',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
          ]}
          page={'nai-flow-view'}
        >
          <TrainingsView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/trainings/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow/trainings' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/trainings' },
          ]}
          page={'nai-flow'}
        >
          <TrainingDetails />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/trainings/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Trainings', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/trainings' },
          ]}
          page={'nai-flow-view'}
        >
          <TrainingDetailsView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/statistics-capture/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
          ]}
          page={'statistics-capture'}
          parentPage={'nai-flow'}
        >
          <StatisticsCapture />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/statistics-capture/schedule',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
            { title: 'Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'statistics-capture'}
          parentPage={'nai-flow-view'}
        >
          <StatisticsCaptureView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/statistics-capture/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
          ]}
          page={'nai-flow'}
        >
          <AddSchedule />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/statistics-capture/schedule/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
            { title: 'Add Schedule', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <AddScheduleView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/statistics-capture/schedule/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
          ]}
          page={'nai-flow'}
        >
          <AddSchedule />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/statistics-capture/schedule/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
          ]}
          page={'nai-flow-view'}
        >
          <AddScheduleView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/statistics-capture/reports',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
            { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
          ]}
          page={'statistics-capture'}
          parentPage={'nai-flow'}
        >
          <StatisticsCaptureReport />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/statistics-capture/reports',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
            { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
          ]}
          page={'statistics-capture'}
          parentPage={'nai-flow-view'}
        >
          <StatisticsCaptureReportView />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow/statistics-capture/reports/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/schedule' },
            { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow/statistics-capture/reports' },
          ]}
          page={'nai-flow'}
        >
          <EditReport />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-flow-view/statistics-capture/reports/:subName/:subId',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Flow', redirect: '/project-management/:name/:id/nai-flow-view/:slug' },
            { title: 'Statistics Capture', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/schedule' },
            { title: 'Reports', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-flow-view/statistics-capture/reports' },
          ]}
          page={'nai-flow-view'}
        >
          <EditReportView />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/add',
      private: true,
      element: (
        <DashboardLayout>
          <AddProject />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-hub',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
          ]}
          page={'project-page'}
        >
          <NaiHub />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-hub/add',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
            { title: 'Add nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
          ]}
        >
          <AddNaiHub />
        </DashboardLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-hub/edit/:subName/:subId/:resourceName/:resourceId/details',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-hub' },
          ]}
          page={'nai-hub-page'}
        >
          <AddNaiHub mode="detail" />
        </TabsLayout>
      ),
    },
    {
      path: 'project-management/:name/:id/nai-hub/edit/:subName/:subId/:resourceName/:resourceId/nai-hub-dashboard',
      private: true,
      element: (
        <TabsLayout
          breadcrumbData={[
            { title: 'Project Management', redirect: '/project-management' },
            { title: ':name', redirect: '/project-management/:name/:id/details' },
            { title: 'nAI Hub', redirect: '/project-management/:name/:id/nai-hub' },
            { title: ':subName', redirect: '/project-management/:name/:id/nai-hub' },
          ]}
          page={'nai-hub-page'}
        >
          <NaiHubDashboard />
        </TabsLayout>
      ),
    },
    {
      path: 'masters',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Division', redirect: '/masters' },
          ]}
        >
          <Division />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/hazard-type',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Hazard Type', redirect: '/masters' },
          ]}
        >
          <HazardType />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/root-cause',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Root Cause', redirect: '/masters' },
          ]}
        >
          <RootCause />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/severity',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Severity', redirect: '/masters' },
          ]}
        >
          <Severity />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/observation-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Observation Status', redirect: '/masters' },
          ]}
        >
          <ObservationStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/statistics-capture-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Statistics Capture Status', redirect: '/masters' },
          ]}
        >
          <StatisticsCaptureStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/statistics-capture-inputs',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Statistics Capture Inputs', redirect: '/masters' },
          ]}
        >
          <StatisticsCaptureInputs />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/incident-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Incident Status', redirect: '/masters' },
          ]}
        >
          <IncidentStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/incident-category',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Incident Category', redirect: '/masters' },
          ]}
        >
          <IncidentCategory />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/incident-severity',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Incident Severity', redirect: '/masters' },
          ]}
        >
          <IncidentSeverity />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Status', redirect: '/masters' },
          ]}
        >
          <InspectionStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-schedule-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Schedule Status', redirect: '/masters' },
          ]}
        >
          <InspectionScheduleStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-non-conformance-status',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Non Conformance Status', redirect: '/masters' },
          ]}
        >
          <InspectionNonConformanceStatus />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-sub-category',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Sub Category', redirect: '/masters' },
          ]}
        >
          <InspectionSubCategory />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-topic',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Topic', redirect: '/masters' },
          ]}
        >
          <InspectionTopic />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/inspection-questions',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Inspection Questions', redirect: '/masters' },
          ]}
        >
          <InspectionQuestions />
        </DashboardLayout>
      ),
    },
    {
      path: 'masters/training-category',
      private: true,
      element: (
        <DashboardLayout
          breadcrumbData={[
            { title: 'Masters', redirect: '/masters' },
            { title: 'Training Category', redirect: '/masters' },
          ]}
        >
          <TrainingCategory />
        </DashboardLayout>
      ),
    },
  ]),
  {
    path: 'check-app',
    element: (
      <LRFLayout>
        <CheckAppInstalled />
      </LRFLayout>
    ),
  },
  {
    path: 'sso',
    private: false,
    element: <SSO />,
  },
  {
    path: '*',
    element: <PageNotFound />,
  },
]
